import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO } from "@/data/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Foundation", to: "/about" },
  { label: "The Cause", to: "/cause" },
  { label: "Event", to: "/event" },
  { label: "Sponsor", to: "/sponsor" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 border-b border-border transition-colors duration-300 ${
        scrolled ? "bg-lightcream/95 backdrop-blur-md" : "bg-lightcream/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={LOGO} alt="The Wayne Walker Heroes & Legacy Golf Classic" className="h-14 w-14 object-contain" />
          <div className="hidden sm:block leading-none">
            <div className="uppercase text-lg text-espresso leading-none" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: "0.02em" }}>Heroes & Legacy</div>
            <div className="label-font text-[10px] text-rust">Golf Classic · Est. 2027</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`label-font text-xs transition-colors hover:text-rust ${
                pathname === n.to ? "text-rust" : "text-espresso/80"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/donate"
            data-testid="nav-donate-cta"
            className="label-font text-xs text-espresso border border-espresso px-5 py-2.5 transition-colors hover:bg-espresso hover:text-cream"
          >
            Donate
          </Link>
          <Link
            to="/register"
            data-testid="nav-register-cta"
            className="label-font text-xs bg-rust text-primary-foreground px-5 py-2.5 border border-espresso transition-transform hover:-translate-y-0.5 hard-shadow-sm"
          >
            Register a Team
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-espresso"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-lightcream border-t border-border px-5 py-6 flex flex-col gap-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="label-font text-sm text-espresso hover:text-rust"
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/donate"
            data-testid="mobile-donate-cta"
            className="label-font text-sm border border-espresso px-5 py-3 text-center text-espresso"
          >
            Donate
          </Link>
          <Link
            to="/register"
            className="label-font text-sm bg-rust text-primary-foreground px-5 py-3 border border-espresso text-center hard-shadow-sm"
          >
            Register a Team
          </Link>
        </div>
      )}
    </header>
  );
}
